import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameHour,
  isSameMinute,
  isSameSecond,
} from 'date-fns';
import { toDate } from 'date-fns-tz';
import { CalendarTimeInterface } from 'further-types/utils';
import { dateToLabel } from './dates';

class CalendarTime implements CalendarTimeInterface {
  public readonly dateTime: string;

  public constructor(calendarDay?: string | Date) {
    if (!calendarDay) {
      this.dateTime = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    } else if (calendarDay instanceof Date) {
      this.dateTime = format(calendarDay, 'yyyy-MM-dd HH:mm:ss');
    } else {
      this.dateTime = calendarDay;
    }
  }

  public static getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public getYear() {
    const dateParts = this.dateTime.split(' ')[0].split('-');
    return Number(dateParts[0]);
  }

  public getDay() {
    const dateParts = this.dateTime.split(' ')[0].split('-');
    return Number(dateParts[2]);
  }

  public getMonth() {
    const dateParts = this.dateTime.split(' ')[0].split('-');
    return Number(dateParts[1]);
  }

  public getHour() {
    const dateParts = this.dateTime.split(' ')[1].split(':');
    return Number(dateParts[0]);
  }

  public getMinute() {
    const dateParts = this.dateTime.split(' ')[1].split(':');
    return Number(dateParts[1]);
  }

  public getSecond() {
    const dateParts = this.dateTime.split(' ')[1].split(':');
    return Number(dateParts[2]);
  }

  public toString() {
    return this.dateTime;
  }

  toJSON() {
    return {
      class: 'CalendarDay',
      date: this.dateTime,
    };
  }

  public toLabel(fallback = 'N/A', format?: string, showAsUTC = false) {
    return dateToLabel(this.dateTime, fallback, false, showAsUTC, format);
  }

  public toReadable(useFullMonthName = false) {
    return this.toLabel(
      '--',
      useFullMonthName ? 'MMMM d, yyyy' : 'MMM d, yyyy',
    );
  }

  public toReadableWithTime(useFullMonthName = false, showAsUTC = true) {
    return this.toLabel(
      '--',
      useFullMonthName ? 'MMMM d, yyyy HH:mm' : 'MMM d, yyyy HH:mm',
      showAsUTC,
    );
  }

  public toUTCTime() {
    return new Date(
      Date.UTC(
        this.getYear(),
        this.getMonth() - 1,
        this.getDay(),
        this.getHour(),
        this.getMinute(),
        this.getSecond(),
        0,
      ),
    );
  }

  public toLocalDateLabel() {
    const browserTimezone = CalendarTime.getBrowserTimezone();
    const date = toDate(this.dateTime, {
      timeZone: browserTimezone,
    });
    return format(date, 'yyyy-MM-dd HH:mm:ss');
  }

  public toLocalDate() {
    return new Date(
      this.getYear(),
      this.getMonth() - 1,
      this.getDay(),
      this.getHour(),
      this.getMinute(),
      this.getSecond(),
      0,
    );
  }

  public isAfter(date: CalendarTime | Date) {
    const calendarTimeDate =
      date instanceof CalendarTime ? date : new CalendarTime(date);

    return isAfter(this.toUTCTime(), calendarTimeDate.toUTCTime());
  }

  public isBefore(date: CalendarTime | Date) {
    const calendarTimeDate =
      date instanceof CalendarTime ? date : new CalendarTime(date);

    return isBefore(this.toUTCTime(), calendarTimeDate.toUTCTime());
  }

  public isSameTime(date: CalendarTime | Date) {
    const calendarTimeDate =
      date instanceof CalendarTime ? date : new CalendarTime(date);
    const thisTime = this.toUTCTime();
    const otherTime = calendarTimeDate.toUTCTime();

    return (
      isSameDay(thisTime, otherTime) &&
      isSameHour(thisTime, otherTime) &&
      isSameMinute(thisTime, otherTime) &&
      isSameSecond(thisTime, otherTime)
    );
  }

  public isSameOrAfter(date: CalendarTime | Date) {
    return this.isAfter(date) || this.isSameTime(date);
  }

  public isSameOrBefore(date: CalendarTime | Date) {
    return this.isBefore(date) || this.isSameTime(date);
  }
}

export default CalendarTime;
